import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect } from 'react';

type BhiDateTimePickerProps = {
  value: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
  disabled?: boolean;
  /**
   * When true, an empty value stays empty: no "now" is auto-filled on mount and
   * the field renders blank until the user picks a date/time. Default false
   * preserves the runtime behavior (auto-fill the next valid slot on mount).
   */
  allowEmpty?: boolean;
};

const TIME_INTERVAL_MINUTES = 15;
const DATE_TIME_FORMAT = 'MM/DD/YYYY hh:mm A';

const alignToTimeInterval = (date: Dayjs): Dayjs => {
  const remainder = date.minute() % TIME_INTERVAL_MINUTES;
  if (remainder === 0) {
    return date.second(0).millisecond(0);
  }
  return date
    .add(TIME_INTERVAL_MINUTES - remainder, 'minute')
    .second(0)
    .millisecond(0);
};

const getDefaultDateTime = (): Dayjs => alignToTimeInterval(dayjs().add(TIME_INTERVAL_MINUTES * 2, 'minute'));

/**
 * Single-field date + time picker. Date and time are chosen in one input
 * (with the popup calendar + clock).
 */
export const BhiDateTimePicker: React.FC<BhiDateTimePickerProps> = ({
  value,
  onChange,
  disabled,
  allowEmpty = false,
}) => {
  useEffect(() => {
    // Auto-fill the next valid slot on mount, unless empty is explicitly allowed.
    if (!allowEmpty && !value) {
      onChange(getDefaultDateTime());
    }
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateTimePicker
        value={value ?? null}
        onChange={onChange}
        disablePast
        format={DATE_TIME_FORMAT}
        disabled={disabled}
        slotProps={{
          textField: {
            fullWidth: true,
            sx: { '& .MuiOutlinedInput-root': { height: '40px' } },
          },
          field: { clearable: allowEmpty },
        }}
      />
    </LocalizationProvider>
  );
};
